import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import { deleteBankAccount } from "../../../backend-services";
import ErrorComponent from "../../Error";
import DangerIcon from "../../../assets/icons/DangerIcon";
import { Spinner } from "flowbite-react";
import { useUserStore } from "../../../state";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "../../../state/useCompanyStore";
import ButtonCTA from "../../Reusable/ButtonCTA";

interface BankAccountDeletePromptProps {
  showPrompt: boolean;
  bankAccountId: number;
  bankName: string;
  accountNumber: string;
  accountName: string;
  onClose: () => void;

  setDeleteSuccess: (deleteSuccess: boolean) => void;
}

export const BankAccountDeletePrompt: React.FC<BankAccountDeletePromptProps> = ({
  showPrompt,
  bankAccountId,
  onClose,
  bankName,
  accountNumber,
  accountName,
  setDeleteSuccess,
}) => {
  const { t } = useTranslation("settings");
  const { fetchCompanyProfile, setCompanyBankAccounts } = useCompanyStore();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const handleDeleteBankAccount = async (index: number) => {
    setIsDeleting(true);
    try {
      const data = await deleteBankAccount(index);
      setCompanyBankAccounts(data);
      fetchCompanyProfile();
      setDeleteSuccess(true);
      onClose();
      setIsDeleting(false);
    } catch (error: any) {
      setErrorMessage("Unable to Delete Bank Account.");
      setIsDeleting(false);
      if (
        error.bankAccount &&
        (error.bankAccount.status === 400 || error.bankAccount.status === 500)
      ) {
        return <ErrorComponent errorCode={500} />;
      }
    }
  };

  const handleKeepBankAccount = () => {
    onClose();
  };
  return (
    <>
      {showPrompt && (
        <div className="bg-gray-900 bg-opacity-50 fixed top-0 right-0 left-0 z-50 h-[100vh]">
          <div className="flex justify-center items-center h-full w-full">
            <div
              className={`fixed top-[25%] z-50 w-[320px] md:w-[457px] p-5 md:p-[30px] bg-white rounded-[12px]`}
            >
              <div className="mt-2 text-center">
                <h2 className="mb-2 leading-6 text-[20px] font-semibold text-[#101828]">
                  Delete Bank Account
                </h2>
                <p className="text-[14px] md:text-[16px] text-[#475467]">
                  You are about to permanently delete bank account. <br />
                  This action cannot be reversed once confirmed.
                </p>

                <div className="bg-[#F2F4F7] flex flex-col text-left p-3 rounded-lg mt-4">
                  <h1 className="font-medium text-[#1D2939] mb-2 flex items-center gap-2">
                    {accountName}
                  </h1>
                  <h1 className=" text-[#475467] flex items-center gap-2">
                    <span>{accountNumber} </span>
                    <svg
                      width={6}
                      height={6}
                      viewBox="0 0 6 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx={3} cy={3} r={3} fill="#D0D5DD" />
                    </svg>
                    <span>{bankName}</span>
                  </h1>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-5 font-semibold">
                <ButtonCTA
                  variant="outline_base"
                  className="w-auto"
                  onClick={handleKeepBankAccount}
                >
                  Cancel
                </ButtonCTA>
                <ButtonCTA
                  variant="danger"
                  className="w-[170px]"
                  disabled={isDeleting}
                  loading={isDeleting}
                  onClick={() => handleDeleteBankAccount(bankAccountId)}
                >
                  Delete Account
                </ButtonCTA>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
